import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode
} from "react";
import { IconX } from "@tabler/icons-react";
import styles from "./IconRail.module.css";

/** Icon glyph spec shared by every rail button (Tabler 20 / 1.5). */
export const RAIL_ICON_SIZE = 20;
export const RAIL_ICON_STROKE = 1.5;

/**
 * Grace window after the pointer leaves the rail before the hover-opened
 * flyout collapses. Long enough to forgive clipping the shell edge or the
 * toolbar→panel gap, short enough that the panel never feels stuck.
 */
const HOVER_CLOSE_GRACE_MS = 260;

/**
 * Dismissal is a PARTIAL collapse: each edge shrinks inward by this much
 * while the card fades out (a full collapse back to 0 reads as intense —
 * the exit should be quick and quiet). Opening still grows from 0.
 */
const CLOSE_COLLAPSE_INSET_PX = 20;

/** Rail badge: a dot (something in this module is "on") or a count. */
export type RailBadge = "dot" | number | null | undefined;

export interface RailModule<Id extends string = string> {
  id: Id;
  label: string;
  /** 20px glyph, or any small node (e.g. a live score numeral). */
  icon: ReactNode;
  badge?: RailBadge;
  /** Panel body for this module. Rendered inside the flyout below the header. */
  render: () => ReactNode;
  /** Optional trailing control in the panel header (before the close button). */
  headerTrailing?: ReactNode;
}

/** Divider-separated tool button that toggles something instead of a panel. */
export interface RailAction {
  id: string;
  label: string;
  icon: ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

export interface IconRailProps<Id extends string = string> {
  /** Which screen edge the rail hugs; the flyout opens toward the canvas. */
  side: "left" | "right";
  ariaLabel: string;
  modules: readonly RailModule<Id>[];
  /** Rendered after a divider, below the modules. */
  actions?: readonly RailAction[];
  /** Single-open contextual panel; null = toolbar only (map fully visible). */
  activeId: Id | null;
  onSelect: (id: Id | null) => void;
  /** Extra class for the outer shell (positioning is the host's job). */
  className?: string;
  /**
   * Bump this when the host opens a module programmatically (canvas pick,
   * demo tour). The flyout pins itself so it doesn't hover-close the moment
   * the pointer — which is somewhere over the map — happens to leave.
   */
  externalOpenToken?: number;
}

/**
 * Edge icon rail: a slim 56px toolbar that opens ONE contextual panel at a
 * time (the map is the hero; panels are visitors). Used for the map tools on
 * the left edge and the property report on the right edge.
 *
 * Panels open on HOVER (mouse/pen): the flyout is a rounded clip that grows
 * vertically to the measured panel height, morphs its height continuously
 * when the pointer rakes across the toolbar (incoming content slides in from
 * the direction of travel), and collapses back on hover-out after a short
 * grace period. Clicking a tool button — or interacting with the panel body
 * — PINS the flyout so it survives the pointer moving to the map (and is the
 * only mode of operation for touch, where hover never fires).
 */
export function IconRail<Id extends string = string>(
  props: IconRailProps<Id>
): ReactElement {
  const {
    side,
    ariaLabel,
    modules,
    actions,
    activeId,
    onSelect,
    className,
    externalOpenToken = 0
  } = props;

  /**
   * What the flyout actually renders. Lags `activeId` while the close
   * animation plays out (content must stay mounted as the clip collapses);
   * `dir` is the direction of travel along the toolbar for the enter slide.
   */
  const [flyout, setFlyout] = useState<{ module: Id | null; dir: -1 | 0 | 1 }>(
    { module: activeId, dir: 0 }
  );
  /** Pinned = ignore hover-out (set by click / panel interaction). */
  const [pinned, setPinned] = useState(false);
  /** Measured natural height of the current panel (header + body, px). */
  const [contentHeight, setContentHeight] = useState(0);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const flyoutRef = useRef<HTMLDivElement | null>(null);
  const centerRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  /**
   * After an explicit click-close the pointer is still parked on THAT tool
   * button; without this hover would instantly reopen what the user just
   * dismissed. Only that one module is suppressed — hovering any other
   * button still opens it — and it clears when the pointer leaves the rail.
   */
  const suppressHoverModuleRef = useRef<Id | null>(null);

  // Programmatic open (canvas pick, tour): pin so hover-out can't close it.
  const lastExternalTokenRef = useRef(externalOpenToken);
  useEffect(() => {
    if (externalOpenToken !== lastExternalTokenRef.current) {
      lastExternalTokenRef.current = externalOpenToken;
      if (activeId != null) setPinned(true);
    }
  }, [externalOpenToken, activeId]);

  const open = activeId != null;
  const order = modules.map((m) => m.id);

  // Render-time state sync (instead of effects) so a module swap never
  // paints a mismatched frame. See react.dev "adjusting state during render".
  if (activeId != null && flyout.module !== activeId) {
    const from = flyout.module == null ? -1 : order.indexOf(flyout.module);
    const to = order.indexOf(activeId);
    if (flyout.module == null) {
      setContentHeight(0);
    }
    setFlyout({
      module: activeId,
      dir: flyout.module == null || to === from ? 0 : to > from ? 1 : -1
    });
  }
  if (!open && pinned) {
    setPinned(false);
  }

  // Measure the live panel (header + scroll host), clamped to the clip's
  // max-height so the driven inline height matches the rendered box.
  useLayoutEffect(() => {
    const el = innerRef.current;
    const clip = flyoutRef.current;
    if (el == null || clip == null) return;
    const read = (): number => {
      const natural = Math.ceil(el.offsetHeight);
      const maxH = Number.parseFloat(window.getComputedStyle(clip).maxHeight);
      return Number.isFinite(maxH) ? Math.min(natural, Math.ceil(maxH)) : natural;
    };
    setContentHeight(read());
    const ro = new ResizeObserver(() => {
      setContentHeight(read());
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [flyout.module]);

  // Clip geometry (height + the lockstep center-collapse transforms) is
  // driven imperatively by this single writer — never through the style
  // prop. Opening requires writing a transition-disabled start state
  // straight to the DOM, and React's style diffing would later skip
  // re-writing values it believes it already rendered.
  useLayoutEffect(() => {
    const clip = flyoutRef.current;
    const center = centerRef.current;
    if (clip == null || center == null || contentHeight === 0) return;
    const half = Math.round(contentHeight / 2);

    if (open && clip.getBoundingClientRect().height < 1) {
      clip.style.transition = "none";
      center.style.transition = "none";
      clip.style.transform = `translateY(${half}px)`;
      center.style.transform = `translateY(${-half}px)`;
      void clip.offsetHeight;
      clip.style.transition = "";
      center.style.transition = "";
    }

    const inset = Math.min(CLOSE_COLLAPSE_INSET_PX, half);
    clip.style.height = open
      ? `${contentHeight}px`
      : `${contentHeight - inset * 2}px`;
    clip.style.transform = open ? "translateY(0px)" : `translateY(${inset}px)`;
    clip.style.setProperty("--flyout-opacity", open ? "1" : "0");
    center.style.transform = open
      ? "translateY(0px)"
      : `translateY(${-inset}px)`;
  }, [open, contentHeight]);

  // Unmount fallback for the close animation in case transitionend never
  // fires (e.g. the rail is display:none'd mid-collapse by chrome-hidden).
  useEffect(() => {
    if (open || flyout.module == null) return;
    const timer = window.setTimeout(() => {
      setFlyout({ module: null, dir: 0 });
      setContentHeight(0);
    }, 480);
    return () => window.clearTimeout(timer);
  }, [open, flyout.module]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current != null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  function cancelScheduledClose(): void {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  function scheduleClose(): void {
    cancelScheduledClose();
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      onSelect(null);
    }, HOVER_CLOSE_GRACE_MS);
  }

  function moduleButton(spec: RailModule<Id>): ReactElement {
    const active = activeId === spec.id;
    return (
      <button
        key={spec.id}
        type="button"
        className={
          styles.toolButton + (active ? ` ${styles.toolButtonActive}` : "")
        }
        aria-label={spec.label}
        aria-pressed={active}
        title={spec.label}
        onPointerEnter={(event) => {
          if (event.pointerType === "touch") return;
          cancelScheduledClose();
          // Hover always switches panels — even while pinned. Pinning only
          // protects against hover-OUT closing the flyout mid-workflow.
          if (active || suppressHoverModuleRef.current === spec.id) return;
          onSelect(spec.id);
        }}
        onClick={() => {
          cancelScheduledClose();
          if (active) {
            suppressHoverModuleRef.current = spec.id;
            onSelect(null);
          } else {
            setPinned(true);
            onSelect(spec.id);
          }
        }}
      >
        {spec.icon}
        <RailBadgeGlyph badge={spec.badge} />
      </button>
    );
  }

  function actionButton(spec: RailAction): ReactElement {
    return (
      <button
        key={spec.id}
        type="button"
        className={
          styles.toolButton +
          (spec.active === true ? ` ${styles.toolButtonActive}` : "")
        }
        aria-label={spec.label}
        aria-pressed={spec.active === true}
        title={spec.label}
        disabled={spec.disabled}
        onClick={spec.onClick}
      >
        {spec.icon}
      </button>
    );
  }

  const activeSpec =
    flyout.module != null
      ? modules.find((m) => m.id === flyout.module) ?? null
      : null;

  return (
    <div
      className={[
        styles.shell,
        side === "right" ? styles.shellRight : styles.shellLeft,
        className ?? ""
      ]
        .filter(Boolean)
        .join(" ")}
      onPointerEnter={(event) => {
        if (event.pointerType === "touch") return;
        cancelScheduledClose();
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "touch") return;
        suppressHoverModuleRef.current = null;
        if (!pinned && activeId != null) {
          scheduleClose();
        }
      }}
    >
      <nav
        className={`viewer-chrome viewer-island ${styles.toolbar}`}
        aria-label={ariaLabel}
      >
        {modules.map(moduleButton)}
        {actions != null && actions.length > 0 ? (
          <>
            <div className={styles.toolbarDivider} aria-hidden="true" />
            {actions.map(actionButton)}
          </>
        ) : null}
      </nav>
      {activeSpec != null ? (
        // Center-out reveal: the clip's height and translateY transition in
        // lockstep so both edges expand outward from the panel's vertical
        // midpoint on open, while the inner wrapper counter-translates to
        // pin the content at its final position — the window grows around
        // it. Dismissal is a quick partial collapse + fade (.flyoutClosing).
        <div
          ref={flyoutRef}
          className={
            styles.flyout + (!open ? ` ${styles.flyoutClosing}` : "")
          }
          onPointerDownCapture={() => {
            // Interacting with the panel body pins it — mid-workflow (slider
            // drags, "draw structure", …) the flyout must survive the
            // pointer heading off to the map.
            setPinned(true);
          }}
          onTransitionEnd={(event) => {
            if (
              event.target === event.currentTarget &&
              event.propertyName === "height" &&
              !open
            ) {
              setFlyout({ module: null, dir: 0 });
              setContentHeight(0);
            }
          }}
        >
          <div ref={centerRef} className={styles.flyoutCenter}>
            <div ref={innerRef} className={styles.flyoutInner}>
              <RailPanelHeader
                icon={activeSpec.icon}
                label={activeSpec.label}
                trailing={activeSpec.headerTrailing}
                onClose={() => {
                  suppressHoverModuleRef.current = activeSpec.id;
                  onSelect(null);
                }}
              />
              {/* Keyed per module: a swap remounts the scroll host (resetting
                  scroll) and restarts the directional enter animation. The
                  slide transform lives on the scroll HOST, not its content —
                  transformed children extend scrollable overflow and would
                  flash a scrollbar mid-animation. */}
              <div
                key={activeSpec.id}
                className={
                  styles.flyoutScroll +
                  (flyout.dir === 1
                    ? ` ${styles.flyoutScrollFromBelow}`
                    : flyout.dir === -1
                      ? ` ${styles.flyoutScrollFromAbove}`
                      : "")
                }
              >
                <div className={styles.flyoutBody}>{activeSpec.render()}</div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RailBadgeGlyph({ badge }: { badge: RailBadge }): ReactElement | null {
  if (badge == null || badge === 0) return null;
  if (badge === "dot") {
    return <span className={styles.badgeDot} aria-hidden="true" />;
  }
  return (
    <span className={styles.badgeCount} aria-hidden="true">
      {badge > 99 ? "99+" : badge}
    </span>
  );
}

/** Small text action for the panel header (e.g. "Reset"). */
export function RailPanelHeaderAction({
  label,
  onClick,
  title
}: {
  label: string;
  onClick: () => void;
  title?: string;
}): ReactElement {
  return (
    <button
      type="button"
      className={styles.panelHeaderAction}
      onClick={onClick}
      title={title}
    >
      {label}
    </button>
  );
}

export interface RailPanelHeaderProps {
  icon: ReactNode;
  label: string;
  trailing?: ReactNode;
  onClose?: () => void;
}

/**
 * Panel title row rendered by the flyout host (cards are body-only). Echoes
 * the rail button that opened it — same glyph, same size — so icon → panel
 * reads as one object.
 */
export function RailPanelHeader({
  icon,
  label,
  trailing,
  onClose
}: RailPanelHeaderProps): ReactElement {
  return (
    <header className={styles.panelHeader}>
      <span className={styles.panelHeaderIcon} aria-hidden="true">
        {icon}
      </span>
      <span className={styles.panelHeaderLabel}>{label}</span>
      {trailing}
      {onClose != null ? (
        <button
          type="button"
          className={styles.panelClose}
          aria-label={`Close ${label}`}
          title="Close"
          onClick={onClose}
        >
          <IconX size={16} stroke={1.5} />
        </button>
      ) : null}
    </header>
  );
}
